import { memo } from 'react';
import { useBoard, useItem, usePane } from '../context';
import { BoardCardBody } from './BoardCardItem';
import { FileBody } from './FileItem';
import { FrameBody } from './FrameItem';
import { ImageBody } from './ImageItem';
import { KanbanBody } from './KanbanItem';
import { LinkBody } from './LinkItem';
import { NoteBody } from './NoteItem';
import { RichTextBody } from './RichTextItem';
import { ShapeBody } from './ShapeItem';
import { StrokeBody } from './StrokeItem';
import { TitleBody } from './TitleItem';

/**
 * One item on the board. Positioned in world units inside the transformed `.world` layer,
 * with transient drag geometry overlaid so the document is only written on release.
 */
export const ItemView = memo(function ItemView({ id }: { id: string }) {
  const board = useBoard();
  const item = useItem(id);
  const transient = usePane((s) => s.transient[id]);
  const selected = usePane((s) => s.selection.has(id));
  const far = usePane((s) => s.settledZoom < 0.12);
  if (!item) return null;
  const g = transient ? { ...item, ...transient } : item;

  let body;
  if (far && item.type !== 'image' && item.type !== 'shape' && item.type !== 'stroke' && item.type !== 'frame') {
    // zoomed far out: text is unreadable anyway, so draw a plain block (much cheaper than editors)
    body = <div className="item-far" />;
  } else
  switch (item.type) {
    case 'image':
      body = <ImageBody item={item} width={g.w} />;
      break;
    case 'note':
      body = <NoteBody item={item} />;
      break;
    case 'title':
      body = <TitleBody item={item} />;
      break;
    case 'richtext':
    case 'table':
    case 'todo':
      body = <RichTextBody item={item} />;
      break;
    case 'kanban':
      body = <KanbanBody item={item} />;
      break;
    case 'file':
      body = <FileBody item={item} />;
      break;
    case 'shape':
      body = <ShapeBody item={item} w={g.w} h={g.h} />;
      break;
    case 'stroke':
      body = <StrokeBody item={item} />;
      break;
    case 'link':
      body = <LinkBody item={item} />;
      break;
    case 'frame':
      body = <FrameBody item={item} />;
      break;
    case 'boardcard':
      body = <BoardCardBody item={item} />;
      break;
  }

  return (
    <div
      className={`item item-${item.type}${selected ? ' is-selected' : ''}${board.isLocked(item) ? ' is-locked' : ''}`}
      data-item-id={id}
      data-color={item.type === 'note' ? item.props.color : undefined}
      style={{
        transform: `translate(${g.x}px, ${g.y}px) rotate(${g.rotation}deg)`,
        width: g.w,
        height: g.h,
      }}
    >
      {body}
    </div>
  );
});
